import React from 'react';

import { eventToLink } from '@app/utils/eventToLink';

export const howWillIKnowWhenItsTime = (
	<>
		<p>
			We have heard from countless pet owners that the death of their pet was worse than the death of their own parents. This might sound
			blasphemous to some, but to many, it’s the cold truth. Deciding to euthanize a pet can feel gut-wrenching, murderous, and immoral.
			Families may feel that they are letting their pet down, or that they are causing their best friend’s death. They forget that euthanasia is
			a gift that, when used appropriately at the right time, prevents further physical suffering for the pet and emotional suffering for the
			family. The hardest part of the experience is making the actual decision, and I’m asked on a daily basis, “Doc, how will I know when it’s
			time?”
		</p>
		<p>
			As veterinarians, our job is to help a family make this difficult decision. There is no perfect moment to make this ultimate choice,
			unless the pet is truly suffering—something we are trying to prevent in the first place. Rather, there is a subjective time period, which
			may be hours, days, weeks, or months, when euthanasia is the appropriate decision. Prior to this time, veterinarians may refuse to
			euthanize a pet because they still have a good quality of life, but after this period passes, we may advocate for euthanasia, because
			their sustained suffering is obvious. During this subjective time, however, the family has to make whatever decision is best for them.
			Some owners need time to come to terms with their pet’s decline, while others want to prevent any unnecessary suffering at all.
		</p>
		<p>
			Every pet owner is different and entitled to their own thoughts and beliefs. After all, you know your pet better than anyone—including
			your veterinarian.
		</p>
	</>
);

export const qualityOfLifeScale = (
	<>
		<p>Concerned about your pet's quality of life? It's not as simple as saying 'when he stops eating' or 'you'll just know’.</p>
		<p>
			Our
			{' '}
			<a
				href="/how-will-i-know-it-is-time/Lap-of-Love-Quality-of-Life-Scale.pdf"
				target="_blank"
				onClick={() => eventToLink('Lap of Love Quality of Life Scale')}
				rel="noopener noreferrer"
			>
				Lap of Love Quality-of-Life Scale
			</a>
			{' '}
			and
			<a
				href="/how-will-i-know-it-is-time/interactive-quality-of-life-assessment-and-diary.pdf"
				target="_blank"
				onClick={() => eventToLink('Quality of Life Daily Assessment')}
				rel="noopener noreferrer"
			>
				{' '}
				Quality-of-Life Daily Assessment
				{' '}
			</a>
			are tools to help you and your family evaluate your pet’s quality of life. Print these PDF's, keep them in plain sight and have multiple
			people in the family use them daily or weekly.
		</p>
	</>
);

export const qualityOfLife = (
	<>
		<p>
			You have probably heard the term “quality of life” in conversations with your family veterinarian or people close to you. Assessing your
			pet’s quality of life, which is subjective, and highly dependent on your dog or cat’s disease process, their personality, and your
			personal beliefs, is difficult.
		</p>
		<p>
			Like humans, every pet will experience and react to changes in their body differently. Their response is also highly dependent on the
			disease process at hand, making in-depth discussions with your regular veterinarian an important part of the process. For example, a pet
			owner should make the decision to euthanize a Yorkshire terrier with congestive heart failure before painful symptoms, such as difficulty
			breathing, arise. Alternatively, an older Labrador retriever with arthritis can be maintained at home with adequate pain management for an
			extended time period.
		</p>
		<p>
			It is important to understand the disease process your pet is experiencing to properly evaluate their quality of life. The common diseases
			articles in the education section on our website contain information about specific changes you can expect with various medical conditions
		</p>
	</>
);

export const painAndAnxiety = (
	<>
		<p>
			Pain is one of the most important topics we discuss in veterinary hospice care. Many professionals believe carnivorous animals, such as
			cats and dogs, do not “hide” their pain, but are not as bothered by pain as humans. This is vastly different from prey animals, such as
			rabbits and guinea pigs, who must hide their pain to prevent being attacked. In addition, animals do not attach emotion to their pain like
			humans. We react to Fluffy’s cancer diagnosis much differently—Fluffy doesn’t know she has a terminal illness, so it bothers us more than
			it bothers her. If you’re interested in learning more about pets’ pain and suffering, read chapter five in Temple Grandin’s book Animals
			in Translation.
		</p>
		<p>
			When considering euthanasia, you should be as concerned about your pet’s anxiety as you are about their pain. Frankly, anxiety can be
			worse than pain to animals. Think about the last time your dog went to the veterinarian. How was his behavior? Was he nervous in the exam
			room? Did he give you that “This is terrible!” look? Now, think back to when he was last hurt, perhaps scraping his paw, or straining a
			muscle after running too hard. My dog acts considerably more distraught when she is anxious than when she is in pain, which is also
			typical for pets who are dying. For example, many end-stage, arthritic dogs begin panting, pacing, whining, and/or crying, but these
			symptoms are due to anxiety, usually secondary to pain. This is akin to being stung by a bee that you do not see in that you may be more
			anxious about not understanding the pain’s origin—and therefore the pain’s duration, and potential worsening—than the pain itself. Due to
			hormonal fluctuations and other factors, these anxiety signs usually worsen at night. The carnivorous dog’s body is telling him that he is
			no longer at the top of the food chain. He has been demoted, and if he lies down, he will become someone else’s dinner. Anti-anxiety
			medications can sometimes help, but the end is usually near for pets at this stage.
		</p>
	</>
);

export const waitingTooLong = (
	<>
		<p>
			We often see an interesting trend that we did not expect when starting our hospice practice. The more times families experience the loss
			of a pet, the sooner they make the decision to euthanize. Owners experiencing a pet’s decline or terminal illness for the first time will
			generally wait until the very end to make the difficult euthanasia decision. They are fearful of euthanizing their pet too soon, and
			giving up without a good fight. Afterward, however, most of these owners regret waiting too long. They reflect back on the past days,
			weeks, or months, and feel guilty for putting their pet through numerous veterinary trips, or uncomfortable medical procedures that did
			not improve their pet’s quality of life. The next time, they recognize their pet’s decline, and are more likely to make the decision at
			the beginning rather than the end of the decline.
		</p>
	</>
);

export const whatAboutANaturalDeath = (
	<>
		<p>
			Yes, some pets peacefully fall asleep and pass naturally on their own, but as in humans, such a peaceful death is rare. Many owners fear
			their pet passing alone, while others do not. Occasionally, we are asked to help families through the natural dying process with their
			pet. For different reasons, these families are opposed to euthanasia. We explain everything we possibly can, including how a natural death
			may look, how long it may take, and what their pet may experience, but inevitably, almost all families regret choosing a natural death.
			Most comment afterward, “I wish I would not have done that. I wish she didn’t have to suffer.”
		</p>
		<p>
			A natural death can be difficult to watch, especially for non-medically oriented people. Most people can more easily watch a human family
			member in pain than their pet. To an extent, we can talk other humans through physical pain or discomfort, but we cannot comfort a pet who
			is suffering. Families find this guilt difficult, and we do our best to not only readily suggest euthanasia when appropriate, but also
			prepare families for a worst-case scenario should they choose to wait. Of course, death is nothing to fear, and your pet happening to pass
			on their own is certainly not a bad thing—it happens in nature frequently!
		</p>
	</>
);

export const weighYourOptionsCarefully = (
	<>
		<p>
			If the most important thing is waiting until the last possible minute to say goodbye to your baby, you will most likely face an emergency,
			stress-filled, sufferable passing for your pet that may not be peaceful, and you may regret waiting too long. If you wish for a peaceful,
			calm, loving, family-oriented, in-home end-of-life experience for your pet, you will probably need to make the decision a little sooner
			than you want. This decision should not be about ending suffering that has already occurred, but about preventing any suffering in the
			first place. Above all, our pets do not deserve to hurt.
		</p>
		<p>
			We are here to help make this time easier for everyone involved. The goal of veterinary hospice care is maintaining comfort, quality of
			life, and the human-animal bond for as long as needed, and we are here for you throughout the entire process.
		</p>
	</>
);
